import { Fragment } from 'react'

interface LegalSection {
  title?: string
  paragraphs?: string[]
  items?: string[]
}

interface LegalDocumentProps {
  title: string
  lastUpdatedLabel: string
  lastUpdated: string
  intro?: string
  sections: LegalSection[]
}

function renderInline(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g)
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold text-foreground">
          {part.slice(2, -2)}
        </strong>
      )
    }
    return <Fragment key={index}>{part}</Fragment>
  })
}

export function LegalDocument({
  title,
  lastUpdatedLabel,
  lastUpdated,
  intro,
  sections,
}: LegalDocumentProps) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-8 sm:py-14">
      <header className="space-y-3 border-b border-border/60 pb-8">
        <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
          {lastUpdatedLabel}
          :
          {lastUpdated}
        </p>
        <h1 className="font-(--font-display) text-4xl tracking-wide text-foreground sm:text-5xl">
          {title}
        </h1>
        {intro
          ? (
              <p className="pt-2 text-base leading-relaxed text-muted-foreground">
                {renderInline(intro)}
              </p>
            )
          : null}
      </header>

      <div className="mt-10 space-y-10">
        {sections.map((section, index) => (
          <section key={index} className="space-y-3 text-justify">
            {section.title
              ? (
                  <h2 className="font-(--font-display) text-xl tracking-wide text-foreground sm:text-2xl">
                    {section.title}
                  </h2>
                )
              : null}
            {section.paragraphs?.map((paragraph, pIndex) => (
              <p
                key={pIndex}
                className="text-sm leading-relaxed text-muted-foreground sm:text-base"
              >
                {renderInline(paragraph)}
              </p>
            ))}
            {section.items && section.items.length > 0
              ? (
                  <ul className="list-disc space-y-1.5 pl-6 text-sm leading-relaxed text-muted-foreground marker:text-muted-foreground/60 sm:text-base">
                    {section.items.map((item, iIndex) => (
                      <li key={iIndex}>{renderInline(item)}</li>
                    ))}
                  </ul>
                )
              : null}
          </section>
        ))}
      </div>
    </div>
  )
}
